import React, { useState } from "react";
import usePolicyCompanyRoles from "../../policy-wizard/usePolicyCompanyRoles";
import PolicyRoleSelectOptions from "../../policy-wizard/PolicyRoleSelectOptions";
import { resolveRoleSelectValue } from "../../../../utils/roleSelectOptions";

/**
 * Reusable approval workflow editor.
 * layout="inline" keeps method + reminder + escalation + parallel on one row;
 * each approval step is Role | SLA | Notify only | Remove on one row.
 */
const LeaveWorkflowEditor = ({
  darkMode,
  workflow,
  onChange,
  showTitle = true,
  autoApprovedMessage,
  showCancellationTab = true,
  reminderLabel = "Reminder days",
  layout = "inline",
  isAttendanceFinalization = false,
}) => {
  const [workflowStepsTab, setWorkflowStepsTab] = useState("approval");
  const { policyRoleOptions, defaultPolicyRoleValue } = usePolicyCompanyRoles();
  const inputCls = `w-full rounded-xl border px-2.5 py-2 text-sm ${
    darkMode
      ? "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-fg))]"
      : "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-fg))]"
  }`;
  const labelCls = `mb-1 block text-xs font-medium ${
    darkMode ? "text-[hsl(var(--ds-muted-fg))]" : "text-[hsl(var(--ds-muted-fg))]"
  }`;
  const stepsKey =
    !showCancellationTab || workflowStepsTab === "approval"
      ? "workflowSteps"
      : "cancellationWorkflowSteps";
  const steps = Array.isArray(workflow[stepsKey]) ? workflow[stepsKey] : [];
  const approvalMethod =
    workflow.approvalMethod === "on_instance"
      ? "auto_approved"
      : workflow.approvalMethod || (isAttendanceFinalization ? "passkey" : "workflow");
  const showWorkflowSteps = approvalMethod === "workflow";

  const setWorkflow = (patch) => onChange({ ...workflow, ...patch });

  const updateStep = (index, patch) => {
    const next = steps.map((step, i) => (i === index ? { ...step, ...patch } : step));
    setWorkflow({ [stepsKey]: next });
  };

  const addStep = () => {
    if (steps.length >= 5) return;
    const nextRole =
      policyRoleOptions[Math.min(steps.length, Math.max(0, policyRoleOptions.length - 1))]
        ?.value ||
      defaultPolicyRoleValue ||
      "MANAGER";
    setWorkflow({
      [stepsKey]: [
        ...steps,
        {
          role: nextRole,
          slaDays: 3,
          notifyOnly: false,
        },
      ],
    });
  };

  const removeStep = (index) => {
    setWorkflow({ [stepsKey]: steps.filter((_, i) => i !== index) });
  };

  const tabBtn = (active) =>
    `rounded-full px-3 py-1.5 text-xs font-medium transition ${
      active
        ? "bg-[hsl(var(--ds-primary))] text-[hsl(var(--ds-primary-fg))]"
        : darkMode
          ? "bg-[hsl(var(--ds-muted))] text-[hsl(var(--ds-muted-fg))]"
          : "bg-[hsl(var(--ds-muted))] text-[hsl(var(--ds-fg))]"
    }`;

  return (
    <div
      className={`rounded-xl border p-3 ${
        darkMode
          ? "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))]/25"
          : "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))]/15"
      }`}
    >
      {showTitle && (
        <h4 className="mb-2 text-sm font-semibold text-[hsl(var(--ds-fg))]">Workflow</h4>
      )}

      <div
        className={
          layout === "inline"
            ? "mb-3 flex flex-wrap items-end gap-3"
            : "mb-3 grid grid-cols-1 gap-3 md:grid-cols-3"
        }
      >
        <div className={layout === "inline" ? "min-w-[10rem] flex-1" : ""}>
          <div className="mb-1 flex items-center gap-1">
            <label className="text-xs font-medium text-[hsl(var(--ds-muted-fg))]">Approval Method</label>
            {isAttendanceFinalization && (
              <span
                title="Auto approved: Locks and unlocks immediately without passkey or approval. Through workflow: Routes lock/unlock requests to the configured approver chain. Passkey: Requires admin passkey to lock and unlock."
                className="inline-flex h-3.5 w-3.5 cursor-help items-center justify-center text-[hsl(var(--ds-muted-fg))]"
                aria-label="Attendance finalization method help"
              >
                ⓘ
              </span>
            )}
          </div>
          <select
            value={approvalMethod}
            onChange={(e) =>
              setWorkflow({
                approvalMethod: e.target.value,
                needWorkflowSteps: false,
              })
            }
            className={inputCls}
          >
            <option value="auto_approved">Auto approved</option>
            <option value="workflow">Through workflow</option>
            {isAttendanceFinalization && <option value="passkey">Passkey</option>}
          </select>
        </div>
        {showWorkflowSteps && (
          <>
            <div className={layout === "inline" ? "w-28 shrink-0" : ""}>
              <label className={labelCls}>{reminderLabel}</label>
              <input
                type="number"
                min={0}
                value={workflow.reminderDays ?? 7}
                onChange={(e) =>
                  setWorkflow({ reminderDays: Number(e.target.value) || 0 })
                }
                className={inputCls}
              />
            </div>
            <div className={layout === "inline" ? "w-32 shrink-0" : ""}>
              <label className={labelCls}>Escalation (days)</label>
              <input
                type="number"
                min={1}
                value={workflow.workflowEscalationAfterDays ?? 3}
                onChange={(e) =>
                  setWorkflow({
                    workflowEscalationAfterDays: Number(e.target.value) || 3,
                  })
                }
                className={inputCls}
              />
            </div>
            <label
              className={`flex items-center gap-2 text-sm whitespace-nowrap ${
                layout === "inline" ? "pb-2" : "md:mt-6"
              }`}
            >
              <input
                type="checkbox"
                checked={!!workflow.workflowParallelApproval}
                onChange={(e) =>
                  setWorkflow({ workflowParallelApproval: e.target.checked })
                }
                className="rounded-md border-[hsl(var(--ds-border))]"
              />
              Parallel approval
            </label>
          </>
        )}
      </div>

      {showWorkflowSteps && (
        <>
          {showCancellationTab ? (
            <div className="mb-3 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => setWorkflowStepsTab("approval")}
                className={tabBtn(workflowStepsTab === "approval")}
              >
                Approval of new request
              </button>
              <button
                type="button"
                title="Used when an already-approved request is cancelled / recalled — not by re-applying the same request."
                onClick={() => setWorkflowStepsTab("cancellation")}
                className={tabBtn(workflowStepsTab === "cancellation")}
              >
                Cancellation
              </button>
            </div>
          ) : (
            <p className="mb-3 text-xs text-[hsl(var(--ds-muted-fg))]">
              Approval steps for this request
            </p>
          )}

          {workflowStepsTab === "cancellation" && showCancellationTab ? (
            <p className="mb-2 text-[11px] text-[hsl(var(--ds-muted-fg))]">
              After a request is approved, use Request cancellation on My requests — it
              follows these cancellation steps (not Recall). Pending requests still use Recall.
            </p>
          ) : null}

          <div className="space-y-2">
            {steps.map((step, index) => (
              <div
                key={`${stepsKey}-${index}`}
                className="flex flex-wrap items-end gap-3 rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] p-2.5"
              >
                <div className="min-w-[12rem] flex-1">
                  <label className={labelCls}>Role</label>
                  <select
                    value={resolveRoleSelectValue(
                      policyRoleOptions,
                      step.role || defaultPolicyRoleValue || "MANAGER"
                    )}
                    onChange={(e) => updateStep(index, { role: e.target.value })}
                    className={inputCls}
                  >
                    <PolicyRoleSelectOptions
                      roleOptions={policyRoleOptions}
                      storedValue={step.role}
                    />
                  </select>
                </div>
                <div className="w-24 shrink-0">
                  <label className={labelCls}>SLA Days</label>
                  <input
                    type="number"
                    min={1}
                    value={step.slaDays ?? 3}
                    onChange={(e) =>
                      updateStep(index, { slaDays: Number(e.target.value) || 3 })
                    }
                    className={inputCls}
                  />
                </div>
                <label className="flex items-center gap-2 pb-2 text-sm whitespace-nowrap">
                  <input
                    type="checkbox"
                    checked={!!step.notifyOnly}
                    onChange={(e) => updateStep(index, { notifyOnly: e.target.checked })}
                    className="rounded-md border-[hsl(var(--ds-border))]"
                  />
                  Notify only
                </label>
                <button
                  type="button"
                  onClick={() => removeStep(index)}
                  className="mb-0.5 rounded-full bg-red-100 px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-200"
                >
                  Remove
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={addStep}
              disabled={steps.length >= 5}
              className="rounded-full bg-[hsl(var(--ds-primary))] px-3 py-1.5 text-xs font-semibold text-[hsl(var(--ds-primary-fg))] disabled:opacity-50"
            >
              + Add Step
            </button>
          </div>
        </>
      )}

      {approvalMethod === "auto_approved" && autoApprovedMessage && (
        <p className="mt-2 text-sm text-[hsl(var(--ds-muted-fg))]">{autoApprovedMessage}</p>
      )}
    </div>
  );
};

export default LeaveWorkflowEditor;
